import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';
import {
  PatchedOpportunityStatusUpdate,
  PatchedOpportunityUpdate,
  AssignmentsService,
  HealthcareEntitiesService,
  UserTypeEnum,
} from '../../../../../js/api';
import type {
  OpportunityNote,
  PaginatedOpportunityNoteList,
  PatchedOpportunityNote,
} from '../../../../../js/api';
import type { FacilitySpecialty } from '../../../../../js/api/models/FacilitySpecialty';
import { SessionContext } from '../../../../../js/app/contexts/SessionContext';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { ActivityLog } from '../../../components/activity-log';
import { BrandSwitch } from '../../../components/BrandSwitch';
import { Button } from '../../../components/button';
import { Dialog, DialogBody } from '../../../components/dialog';
import { DrawerCloseButton } from '../../../components/drawer';
import { DrawerSectionHeader, DrawerField } from '../../../components/drawer-components';
import type { CodeDefinitionOption } from '../../../components/dropdown-select';
import { DropdownSelect } from '../../../components/dropdown-select';
import { InlineDateEdit } from '../../../components/inline-date-edit';
import { InlineTextEdit } from '../../../components/inline-text-edit';
import { PageHeader } from '../../../components/PageHeader';
import { SectionHeader } from '../../../components/SectionHeader';
import { StatusTransitionModal } from '../../../components/status-transition-modal';
import { StatusPill } from '../../../components/StatusPill';
import { Tabs, TabsList, TabsButton, TabsContent } from '../../../components/tabs';
import { TooltippedText } from '../../../components/tooltipped-text';
import { MENU_BADGES_QUERY_KEY, QUEUE_NOTIFICATIONS_QUERY_KEY } from '../../../constants/queryKeys';
import { StatusCodes, WorkflowScopes } from '../../../constants/workflows';
import {
  useCodeDefinitionsAsMaps,
  useCodeDefinitionsByCategories,
} from '../../../hooks/useCodeDefinitions';
import { useCollapseOnOverflow } from '../../../hooks/useCollapseOnOverflow';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useStaffOrHasPermission } from '../../../hooks/useStaffOrHasPermission';
import { StatusMetadata, useWorkflowMetadata } from '../../../hooks/useWorkflowMetadata';
import type { ActivityStatusHistory, ActivityHistory } from '../../../utils/activity-types';
import { formatDateTo24Hr, parseApiLocalDate, parseFormDate } from '../../../utils/date';
import { DynamicFieldFactory, type DynamicField } from '../../../utils/DynamicFieldFactory';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';
import { StatusTransitionButtons } from '../../../utils/statusTransitions';
import { TRANSITION_OPACITY } from '../../../utils/transitionStyles';
import { EditFacilityDialog } from '../EditFacilityDialog';
import { EditSpecialtyDialog } from '../EditSpecialtyDialog';
import {
  enrichDynamicFieldsWithChoices,
  FACILITY_DISPLAY_CODE_CATEGORIES,
} from '../enrichFacilityDynamicFields';
import { FACILITY_QUERY_KEY, FACILITY_TYPE_FIELDS_QUERY_KEY } from '../Facilities/queryKeys';
import { FacilityDetailsPanel } from '../FacilityDetailsPanel';
import { InviteProviderDialog } from './InviteProviderDialog';
import OpportunityForm from './OpportunityForm';
import {
  ORGANIZATION_OPPORTUNITIES_LIST_QUERY_KEY,
  STAFF_OPPORTUNITIES_LIST_QUERY_KEY,
  OPPORTUNITY_NOTES_QUERY_KEY,
  OPPORTUNITY_QUERY_KEY,
  OPPORTUNITY_STATUS_HISTORY_QUERY_KEY,
  OPPORTUNITY_CHANGE_HISTORY_QUERY_KEY,
} from './queryKeys';

type OpportunityDrawerProps = {
  opportunityId: string;
  onClose?: () => void;
  listQueryKeyBase?: string;
  linkContext?: 'organization' | 'staff';
};

const WORKFLOWS_META_QUERY_KEY = 'assignments.opportunity';

function useDebouncedFn<T extends (...args: any[]) => void>(fn: T, delayMs: number) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  return useMemo(() => {
    let t: any;
    const debounced = (...args: any[]) => {
      clearTimeout(t);
      t = setTimeout(() => fnRef.current(...args), delayMs);
    };
    debounced.flush = (...args: any[]) => {
      clearTimeout(t);
      fnRef.current(...args);
    };
    debounced.cancel = () => clearTimeout(t);
    return debounced as T & { flush: T; cancel: () => void };
  }, [delayMs]);
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function OpportunityDrawer({
  opportunityId,
  onClose,
  listQueryKeyBase,
  linkContext = 'organization',
}: OpportunityDrawerProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const opportunitiesListQueryKeys = useMemo(() => {
    const base = listQueryKeyBase || ORGANIZATION_OPPORTUNITIES_LIST_QUERY_KEY;
    return Array.from(
      new Set([base, ORGANIZATION_OPPORTUNITIES_LIST_QUERY_KEY, STAFF_OPPORTUNITIES_LIST_QUERY_KEY])
    );
  }, [listQueryKeyBase]);
  const isMobile = useIsMobile();
  const drawerRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<string>('details');
  const [autoMatch, setAutoMatch] = useState<boolean | null>(null);
  const [maxAcceptedProviders, setMaxAcceptedProviders] = useState<string>('');
  const lastSavedMaxAcceptedProvidersRef = useRef<number | null>(null);
  const isSavingMaxAcceptedProvidersRef = useRef(false);
  const isMaxAcceptedProvidersDirtyRef = useRef(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const lastSavedStartDateRef = useRef<string | null>(null);
  const isSavingStartDateRef = useRef(false);
  const isStartDateDirtyRef = useRef(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [pendingTargetStatus, setPendingTargetStatus] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFacilityEditDialogOpen, setIsFacilityEditDialogOpen] = useState(false);
  const [isInviteProviderOpen, setIsInviteProviderOpen] = useState(false);
  const [isEditSpecialtyDialogOpen, setIsEditSpecialtyDialogOpen] = useState(false);
  const [headerProps, setHeaderProps] = useState<{
    title: string;
    buttonHtml: React.ReactNode;
  } | null>(null);

  const { user } = useContext(SessionContext);
  const canEditFacility = Boolean(
    user?.user_type === UserTypeEnum.STAFF || user?.group_names?.includes('manage_facilities')
  );

  const { data: codeDefinitionMaps } = useCodeDefinitionsAsMaps([
    'Specialty',
    'Degree',
    'WorkType',
    'ShiftRateTypes',
  ]);

  const {
    data: opportunity,
    isLoading: isLoadingOpportunity,
    error: opportunityError,
  } = useQuery({
    queryKey: [OPPORTUNITY_QUERY_KEY, opportunityId],
    queryFn: () => AssignmentsService.assignmentsOpportunitiesRetrieve({ id: opportunityId }),
    enabled: !!opportunityId,
  });

  const opportunityFacilityId = opportunity?.facility?.id;

  const tabs = useMemo(() => {
    if (opportunityFacilityId) {
      return [
        { code: 'details', label: 'Details' as const },
        { code: 'facility-info', label: 'Facility Info' as const },
        { code: 'specialty', label: 'Specialty' as const },
      ];
    }
    return [
      { code: 'details', label: 'Details' as const },
      { code: 'specialty', label: 'Specialty' as const },
    ];
  }, [opportunityFacilityId]);

  // Reset active tab when switching opportunities
  useEffect(() => {
    setActiveTab('details');
  }, [opportunityId]);

  const { ref: tabsMeasureRef, collapsed: isOverflowCollapsed } =
    useCollapseOnOverflow<HTMLDivElement>({
      observeChildren: true,
      forceCollapse: isMobile,
      constraintRef: drawerRef,
      recheckOn: { id: opportunityId, tabs },
    });

  const { data: facilityPanelCodeDefinitions } = useCodeDefinitionsByCategories([
    ...FACILITY_DISPLAY_CODE_CATEGORIES,
  ]);

  const { data: facilityPanelData, isLoading: isFacilityPanelLoading } = useQuery({
    queryKey: [FACILITY_QUERY_KEY, opportunityFacilityId],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilitiesRetrieve({
        id: opportunityFacilityId as string,
      }),
    enabled: Boolean(opportunityFacilityId),
  });

  const { data: facilityTypeFieldsCatalog } = useQuery({
    queryKey: [FACILITY_TYPE_FIELDS_QUERY_KEY],
    queryFn: () => HealthcareEntitiesService.healthcareEntitiesFacilityTypeFieldsList({}),
    enabled: Boolean(facilityPanelData),
  });

  const { data: facilitySpecialtiesData, isLoading: isFacilitySpecialtiesLoading } = useQuery({
    queryKey: ['facilitySpecialties', opportunityFacilityId],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilitySpecialtiesList({
        facility: opportunityFacilityId as string,
      }),
    enabled: Boolean(opportunityFacilityId),
  });

  const facilityTypesWithDynamicFields = useMemo(() => {
    if (!facilityTypeFieldsCatalog?.results) {
      return {};
    }
    return facilityTypeFieldsCatalog.results.reduce((acc: Record<string, any[]>, item) => {
      const parsed =
        typeof item.dynamic_fields === 'string'
          ? (JSON.parse(item.dynamic_fields || '[]') as any[])
          : (item.dynamic_fields as any[]);
      acc[item.facility_type] = parsed;
      return acc;
    }, {});
  }, [facilityTypeFieldsCatalog]);

  const facilityPanelCodeDefMaps = useMemo(() => {
    if (!facilityPanelCodeDefinitions) {
      return {} as Record<string, Record<string, string>>;
    }
    const maps: Record<string, Record<string, string>> = {};
    Object.entries(facilityPanelCodeDefinitions).forEach(([category, defs]) => {
      maps[category] = {};
      defs.forEach((def) => {
        maps[category][def.code] = def.title;
      });
    });
    return maps;
  }, [facilityPanelCodeDefinitions]);

  const enrichedOpportunityFacility = useMemo(() => {
    if (!facilityPanelData || !facilityPanelCodeDefinitions) {
      return null;
    }
    const rawFields = facilityPanelData.facility_type_fields || [];
    const facilityTypeFieldsArray =
      typeof rawFields === 'string' ? (JSON.parse(rawFields || '[]') as any[]) : rawFields;
    return {
      ...facilityPanelData,
      facility_type_fields: enrichDynamicFieldsWithChoices(
        facilityTypeFieldsArray,
        facilityPanelCodeDefinitions
      ),
    };
  }, [facilityPanelData, facilityPanelCodeDefinitions]);

  const { statuses: workflowStatuses } = useWorkflowMetadata(WORKFLOWS_META_QUERY_KEY);

  const hasLifeSupportCertification = (opportunity?.life_support_certifications?.length ?? 0) > 0;
  const hasBoardCertification = (opportunity?.board_certifications?.length ?? 0) > 0;
  const hasRates =
    (opportunity?.shift_rate_types?.length ?? 0) > 0 &&
    (opportunity?.shift_rate_types ?? []).every(
      (type) =>
        (opportunity?.settings?.[`max_rate_${type}` as keyof typeof opportunity.settings] ??
          null) !== null
    );
  const allowPublish: boolean = hasLifeSupportCertification && hasBoardCertification && hasRates;
  const disabledStatusMap: Partial<Record<StatusCodes, boolean>> = {
    [StatusCodes.NEW]: !allowPublish,
  };
  const { data: notesData } = useQuery({
    queryKey: [OPPORTUNITY_NOTES_QUERY_KEY, opportunityId],
    queryFn: () =>
      AssignmentsService.assignmentsOpportunitiesNotesList({
        id: opportunityId,
        ordering: '-created_at', // Most recent first
      }),
    enabled: !!opportunityId,
    select: (data: PaginatedOpportunityNoteList | OpportunityNote | OpportunityNote[]) => {
      if (!data) {
        return [];
      }
      // Handle array of notes
      if (Array.isArray(data)) {
        return data;
      }
      // Handle paginated response
      if ('results' in data) {
        return data.results || [];
      }
      // Handle single note
      return [data as OpportunityNote];
    },
  });

  const { data: statusHistory } = useQuery({
    queryKey: [OPPORTUNITY_STATUS_HISTORY_QUERY_KEY, opportunityId],
    queryFn: () =>
      AssignmentsService.assignmentsOpportunitiesStatusHistoryRetrieve({
        id: opportunityId,
        sort: 'desc',
      }),
    enabled: !!opportunityId,
  });

  const { data: changeHistory } = useQuery({
    queryKey: [OPPORTUNITY_CHANGE_HISTORY_QUERY_KEY, opportunityId],
    queryFn: () =>
      AssignmentsService.assignmentsOpportunitiesChangeHistoryRetrieve({
        id: opportunityId,
        sort: 'desc',
      }),
    enabled: !!opportunityId,
  });

  useEffect(() => {
    if (opportunity) {
      setAutoMatch(opportunity.settings?.auto_match ?? false);
      // Store the initial value to compare against in the debounced effect
      if (!isSavingMaxAcceptedProvidersRef.current) {
        setMaxAcceptedProviders(opportunity.settings?.max_accepted_connections?.toString() ?? '');
        lastSavedMaxAcceptedProvidersRef.current =
          opportunity.settings?.max_accepted_connections ?? null;
        isMaxAcceptedProvidersDirtyRef.current = false;
      }
      if (!isSavingStartDateRef.current) {
        setStartDate(parseApiLocalDate(opportunity.starts_on));
        lastSavedStartDateRef.current = opportunity.starts_on ?? null;
        isStartDateDirtyRef.current = false;
      }
    }
  }, [opportunity]);

  const updateAutoMatchMutation = useMutation({
    mutationFn: async (value: boolean) => {
      return AssignmentsService.assignmentsOpportunitiesPartialUpdate({
        id: opportunityId,
        requestBody: {
          settings: {
            ...opportunity?.settings,
            auto_match: value,
          },
        } as PatchedOpportunityUpdate,
      });
    },
    onSuccess: async (data) => {
      const id = opportunityId; // id isn't in variables for this one as currently written
      queryClient.setQueryData([OPPORTUNITY_QUERY_KEY, id], data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, id] }),
        ...opportunitiesListQueryKeys.map((k) => queryClient.invalidateQueries({ queryKey: [k] })),
        queryClient.invalidateQueries({
          queryKey: [OPPORTUNITY_STATUS_HISTORY_QUERY_KEY, id],
        }),
        queryClient.invalidateQueries({
          queryKey: [OPPORTUNITY_CHANGE_HISTORY_QUERY_KEY, id],
        }),
      ]);
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating auto match' });
      // Revert on error
      if (opportunity) {
        setAutoMatch(opportunity.settings?.auto_match ?? false);
      }
    },
  });

  const updateMaxAcceptedProvidersMutation = useMutation({
    mutationFn: async (value: number | null) => {
      return AssignmentsService.assignmentsOpportunitiesPartialUpdate({
        id: opportunityId,
        requestBody: {
          settings: {
            ...opportunity?.settings,
            max_accepted_connections: value,
          },
        } as PatchedOpportunityUpdate,
      });
    },
    onSuccess: async (data) => {
      const id = opportunityId;
      queryClient.setQueryData([OPPORTUNITY_QUERY_KEY, id], data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, id] }),
        ...opportunitiesListQueryKeys.map((k) => queryClient.invalidateQueries({ queryKey: [k] })),
        queryClient.invalidateQueries({
          queryKey: [OPPORTUNITY_STATUS_HISTORY_QUERY_KEY, id],
        }),
        queryClient.invalidateQueries({
          queryKey: [OPPORTUNITY_CHANGE_HISTORY_QUERY_KEY, id],
        }),
      ]);
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating maximum accepted providers' });
      // Revert on error
      if (opportunity) {
        setMaxAcceptedProviders(opportunity.settings?.max_accepted_connections?.toString() ?? '');
        lastSavedMaxAcceptedProvidersRef.current =
          opportunity.settings?.max_accepted_connections ?? null;
      }
    },
  });

  const updateStartDateMutation = useMutation({
    mutationFn: async (value: string | null) => {
      return AssignmentsService.assignmentsOpportunitiesPartialUpdate({
        id: opportunityId,
        requestBody: {
          starts_on: value,
        } as PatchedOpportunityUpdate,
      });
    },
    onSuccess: async (data) => {
      const id = opportunityId;
      queryClient.setQueryData([OPPORTUNITY_QUERY_KEY, id], data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, id] }),
        ...opportunitiesListQueryKeys.map((k) => queryClient.invalidateQueries({ queryKey: [k] })),
        queryClient.invalidateQueries({
          queryKey: [OPPORTUNITY_STATUS_HISTORY_QUERY_KEY, id],
        }),
        queryClient.invalidateQueries({
          queryKey: [OPPORTUNITY_CHANGE_HISTORY_QUERY_KEY, id],
        }),
      ]);
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating start date' });
      // Revert on error
      if (opportunity) {
        setStartDate(parseApiLocalDate(opportunity.starts_on));
        lastSavedStartDateRef.current = opportunity.starts_on ?? null;
      }
    },
  });

  const statusUpdateMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      statusCode: string | null;
      reasonCode?: string | null;
      comment?: string;
    }) => {
      const statusUpdateData: PatchedOpportunityStatusUpdate = {
        code: data.statusCode || undefined,
        reason_code: data.reasonCode ?? null,
        comment: data.comment || null,
      };
      return AssignmentsService.assignmentsOpportunitiesStatusUpdatePartialUpdate({
        id: data.id,
        requestBody: statusUpdateData,
      });
    },
    onSuccess: async (updatedOpportunity, variables) => {
      const { id } = variables;
      toast.success('Opportunity status successfully updated');
      queryClient.setQueryData([OPPORTUNITY_QUERY_KEY, id], updatedOpportunity);
      const invalidations = [
        queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, id] }),
        ...opportunitiesListQueryKeys.map((k) => queryClient.invalidateQueries({ queryKey: [k] })),
        queryClient.invalidateQueries({
          queryKey: [OPPORTUNITY_STATUS_HISTORY_QUERY_KEY, id],
        }),
        queryClient.invalidateQueries({
          queryKey: [OPPORTUNITY_CHANGE_HISTORY_QUERY_KEY, id],
        }),
        queryClient.invalidateQueries({ queryKey: ['staffingNeeds', 'list'] }),
        // Invalidate queue notification badges for this scope
        queryClient.invalidateQueries({
          queryKey: [QUEUE_NOTIFICATIONS_QUERY_KEY, WorkflowScopes.Opportunity],
        }),
        // Invalidate global menu badges
        queryClient.invalidateQueries({ queryKey: [MENU_BADGES_QUERY_KEY] }),
      ];
      if (updatedOpportunity.need?.id) {
        invalidations.push(
          queryClient.invalidateQueries({ queryKey: ['need', updatedOpportunity.need?.id] })
        );
      }
      await Promise.all(invalidations);
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Failed to update opportunity status' });
    },
  });

  const handleAutoMatchChange = (checked: boolean) => {
    setAutoMatch(checked);
    updateAutoMatchMutation.mutate(checked);
  };

  const handleMaxAcceptedProvidersChange = (value: string) => {
    setMaxAcceptedProviders(value);
    isMaxAcceptedProvidersDirtyRef.current = true;
  };

  const maxAcceptedProvidersCommit = useCallback(
    async (val?: number | null) => {
      const numericValue =
        val !== undefined ? val : maxAcceptedProviders === '' ? null : Number(maxAcceptedProviders);
      if (
        numericValue === lastSavedMaxAcceptedProvidersRef.current ||
        isSavingMaxAcceptedProvidersRef.current ||
        !isMaxAcceptedProvidersDirtyRef.current
      ) {
        return;
      }

      try {
        isSavingMaxAcceptedProvidersRef.current = true;
        await updateMaxAcceptedProvidersMutation.mutateAsync(numericValue);
        lastSavedMaxAcceptedProvidersRef.current = numericValue;
        isMaxAcceptedProvidersDirtyRef.current = false;
      } catch (e) {
        console.error('Max accepted providers save failed', e);
      } finally {
        isSavingMaxAcceptedProvidersRef.current = false;
      }
    },
    [updateMaxAcceptedProvidersMutation, maxAcceptedProviders]
  );

  const debouncedMaxAcceptedProvidersCommit = useDebouncedFn(maxAcceptedProvidersCommit, 600);

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    isStartDateDirtyRef.current = true;
  };

  const startDateCommit = useCallback(
    async (val?: Date | null) => {
      const dateToSave = val !== undefined ? val : startDate;
      const dateStr = parseFormDate(dateToSave);

      if (
        dateStr === lastSavedStartDateRef.current ||
        isSavingStartDateRef.current ||
        !isStartDateDirtyRef.current
      ) {
        return;
      }

      try {
        isSavingStartDateRef.current = true;
        await updateStartDateMutation.mutateAsync(dateStr);
        lastSavedStartDateRef.current = dateStr;
        isStartDateDirtyRef.current = false;
      } catch (e) {
        console.error('Start date save failed', e);
      } finally {
        isSavingStartDateRef.current = false;
      }
    },
    [updateStartDateMutation, startDate]
  );

  const debouncedStartDateCommit = useDebouncedFn(startDateCommit, 600);

  const handleStartDateBlur = (value?: Date | null) => {
    debouncedStartDateCommit.flush(value);
  };

  const handleMaxAcceptedProvidersBlur = (value: string) => {
    if (!opportunity) {
      return;
    }

    // Validate that it's a number if not empty
    if (value !== '' && isNaN(Number(value))) {
      return;
    }

    debouncedMaxAcceptedProvidersCommit.flush(value === '' ? null : Number(value));
  };

  // Flush when leaving the page or unmounting (Safari-friendly)
  useEffect(() => {
    const onPageHide = () => {
      debouncedMaxAcceptedProvidersCommit.flush();
      debouncedStartDateCommit.flush();
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onPageHide);
      // Flush on unmount to ensure pending changes are saved
      debouncedMaxAcceptedProvidersCommit.flush();
      debouncedStartDateCommit.flush();
    };
  }, [debouncedMaxAcceptedProvidersCommit, debouncedStartDateCommit]);

  // Save on click/tap outside (covers “no blur” cases)
  useEffect(() => {
    const handler = () => {
      debouncedMaxAcceptedProvidersCommit.flush();
      debouncedStartDateCommit.flush();
    };
    document.addEventListener('mousedown', handler, true);
    document.addEventListener('touchstart', handler, true);
    return () => {
      document.removeEventListener('mousedown', handler, true);
      document.removeEventListener('touchstart', handler, true);
    };
  }, [debouncedMaxAcceptedProvidersCommit, debouncedStartDateCommit]);

  // Handle duplicate click
  const handleDuplicateClick = () => {
    navigate(`/app/organization/opportunities/new?duplicate=${opportunityId}`);
  };

  const handleStatusEditClick = () => {
    setPendingTargetStatus(null);
    setIsStatusModalOpen(true);
  };

  // Handle status modal confirm
  const handleStatusModalConfirm = async (data: {
    statusCode: string | null;
    reasonCode: string | null;
    comment?: string;
  }) => {
    if (!opportunityId) {
      toast.error('Opportunity ID not found');
      return;
    }

    await statusUpdateMutation.mutateAsync({
      id: opportunityId,
      statusCode: data.statusCode,
      reasonCode: data.reasonCode,
      comment: data.comment,
    });
    setIsStatusModalOpen(false);
    setPendingTargetStatus(null);
  };

  const handleStatusModalClose = () => {
    setIsStatusModalOpen(false);
    setPendingTargetStatus(null);
  };

  const opportunityFacilitySpecialty = useMemo(() => {
    if (!opportunityFacilityId || !opportunity?.specialty) {
      return null;
    }

    const specialtyLabel =
      (codeDefinitionMaps?.Specialty || {})[opportunity.specialty] || opportunity.specialty;

    const matching = facilitySpecialtiesData?.results?.find(
      (fs: FacilitySpecialty) => String(fs.specialty) === String(opportunity.specialty)
    );
    if (!matching) {
      return null;
    }

    const parsedFields: DynamicField[] =
      typeof matching.dynamic_fields === 'string'
        ? (JSON.parse(matching.dynamic_fields || '[]') as DynamicField[])
        : (matching.dynamic_fields as DynamicField[]);

    return {
      ...matching,
      title: specialtyLabel,
      dynamic_fields: facilityPanelCodeDefinitions
        ? enrichDynamicFieldsWithChoices(parsedFields, facilityPanelCodeDefinitions)
        : parsedFields,
    };
  }, [
    opportunityFacilityId,
    opportunity?.specialty,
    facilitySpecialtiesData?.results,
    facilityPanelCodeDefinitions,
    codeDefinitionMaps?.Specialty,
  ]);

  const hasInvitePermission = useStaffOrHasPermission('create__providers__provider');

  if (isLoadingOpportunity) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <div className="tw:text-zinc-500 tw:dark:text-zinc-400">Loading opportunity details...</div>
      </div>
    );
  }

  if (opportunityError) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <div className="tw:text-stoplight-danger">
          Error loading opportunity details. Please try again.
        </div>
      </div>
    );
  }

  if (!opportunity) {
    return null;
  }

  const isClosed = opportunity.current_status?.code == StatusCodes.CLOSED;
  const isDraft = opportunity.current_status?.code == StatusCodes.DRAFT;
  const specialtyMap = codeDefinitionMaps?.Specialty || {};
  const degreeMap = codeDefinitionMaps?.Degree || {};
  const shiftRateTypeMap = codeDefinitionMaps?.ShiftRateTypes || {};

  const specialtyDrawerLabel =
    opportunityFacilitySpecialty?.title ||
    specialtyMap[opportunity.specialty] ||
    opportunity.specialty;

  const canInviteProvider = !isClosed && !isDraft && hasInvitePermission;

  const needsListBasePath = linkContext === 'staff' ? '/staff/needs' : '/app/organization/needs';

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    // Refresh opportunity data after closing modal
    queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, opportunityId] });
    opportunitiesListQueryKeys.forEach((k) => {
      void queryClient.invalidateQueries({ queryKey: [k] });
    });
  };

  const handleEditSuccess = () => {
    // Refresh opportunity data after successful edit
    queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, opportunityId] });
    opportunitiesListQueryKeys.forEach((k) => {
      void queryClient.invalidateQueries({ queryKey: [k] });
    });
    setIsEditModalOpen(false);
  };

  return (
    <>
      <StatusTransitionModal
        open={isStatusModalOpen}
        onClose={handleStatusModalClose}
        onConfirm={handleStatusModalConfirm}
        initialStatusCode={opportunity?.current_status?.code || null}
        targetStatusCode={pendingTargetStatus}
        workflowScope="assignments.opportunity"
        isPending={statusUpdateMutation.isPending}
        currentReasonCode={null}
        currentStatusTitle={opportunity?.current_status?.title || null}
      />
      <InviteProviderDialog
        open={isInviteProviderOpen}
        onClose={() => setIsInviteProviderOpen(false)}
        opportunityId={opportunityId}
      />
      {opportunityFacilityId && enrichedOpportunityFacility && (
        <EditFacilityDialog
          open={isFacilityEditDialogOpen}
          onClose={() => setIsFacilityEditDialogOpen(false)}
          facilityId={opportunityFacilityId}
          initialData={enrichedOpportunityFacility}
          onSuccess={() => {
            setIsFacilityEditDialogOpen(false);
            queryClient.invalidateQueries({
              queryKey: [FACILITY_QUERY_KEY, opportunityFacilityId],
            });
            queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, opportunityId] });
            opportunitiesListQueryKeys.forEach((k) => {
              void queryClient.invalidateQueries({ queryKey: [k] });
            });
          }}
          facilityTypesWithDynamicFields={facilityTypesWithDynamicFields}
        />
      )}
      <Dialog
        open={isEditModalOpen}
        onClose={handleEditModalClose}
        className="tw:!max-w-6xl tw:!max-h-[calc(100vh-4rem)] tw:!flex tw:!flex-col tw:!p-0"
      >
        <div
          className="tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:overflow-hidden"
          data-testid="opportunity-edit-dialog"
        >
          {headerProps && (
            <div className="tw:flex-shrink-0">
              <PageHeader
                className="tw:rounded-t-2xl"
                title={headerProps.title}
                buttonHtml={headerProps.buttonHtml}
              />
            </div>
          )}
          <div className="tw:flex-1 tw:overflow-y-auto tw:min-h-0 tw:bg-zinc-50">
            <DialogBody className="tw:mt-0 ">
              <OpportunityForm
                opportunityId={opportunityId}
                forceEditMode={true}
                onSuccess={handleEditSuccess}
                onCancel={handleEditModalClose}
                renderHeader={false}
                onHeaderRender={setHeaderProps}
              />
            </DialogBody>
          </div>
        </div>
      </Dialog>
      <div ref={drawerRef} className="tw:flex tw:flex-col tw:mt-6" data-testid="opportunity-drawer">
        {/* Top Section */}
        <div className="tw:grid tw:grid-cols-1 tw:sm:grid-cols-12 tw:gap-6">
          {/* Header block (rows 1-5) */}
          <div className="tw:flex tw:flex-col tw:leading-tight tw:sm:col-span-11">
            {/* Row 1: Opportunity code (and close on mobile) */}
            <div className="tw:flex tw:items-center tw:gap-2">
              <span className="tw:text-lg tw:font-bold tw:min-w-0 tw:truncate">
                {opportunity.code}
              </span>
              <TooltippedText
                tooltipContent="Copy code"
                tooltipClassName="tw:bg-brand-green-2 tw:text-zinc-700"
                placement="bottom"
              >
                <button
                  type="button"
                  className="tw:text-zinc-600 tw:hover:text-zinc-600 tw:bg-transparent tw:border-none tw:p-0 tw:cursor-pointer"
                  onClick={() => {
                    navigator.clipboard.writeText(opportunity.code || '');
                    toast.success('Copied', { autoClose: 1500 });
                  }}
                >
                  <i className="far fa-copy" />
                </button>
              </TooltippedText>
              {onClose && (
                <DrawerCloseButton
                  onClick={onClose}
                  className="tw:shrink-0 tw:sm:hidden"
                  data-testid="opportunity-drawer-close"
                />
              )}
            </div>
            <div className="tw:sr-only" data-testid="opportunity-drawer-code">
              {opportunity.code}
            </div>

            {/* Row 2: Facility name */}
            <div className="tw:flex tw:items-center tw:gap-2 tw:min-w-0 tw:mb-2">
              <i className="fa-solid fa-hospital tw:shrink-0 tw:text-brand-green-0" />
              <span className="tw:truncate">
                {opportunity.facility?.name || 'Unknown Facility'}
              </span>
            </div>

            {/* Row 3: City/State + Specialty */}
            <div className="tw:flex tw:flex-col tw:gap-2 tw:sm:flex-row tw:mb-2 tw:text-zinc-500">
              <div className="tw:flex tw:items-center tw:gap-2 tw:min-w-0">
                <i className="fa-solid fa-location-dot tw:shrink-0" />
                <span className="tw:truncate">
                  {getFacilityLocationDisplayValue(opportunity.facility) || '--'}
                </span>
              </div>
              <div className="tw:flex tw:items-center tw:gap-2 tw:min-w-0">
                <i className="fa-solid fa-stethoscope tw:shrink-0" />
                <span className="tw:truncate">
                  {specialtyMap[opportunity.specialty] || opportunity.specialty || '--'}
                </span>
              </div>
            </div>

            {/* Row 4: Status + edit */}
            <div className="tw:flex tw:items-center tw:gap-2 tw:mb-2">
              <div className="tw:flex tw:items-center tw:gap-2">
                <StatusPill
                  scope={WorkflowScopes.Opportunity}
                  status={opportunity.current_status || 'Unknown'}
                />
              </div>
              <button
                type="button"
                onClick={handleStatusEditClick}
                className={twMerge(
                  'tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0',
                  TRANSITION_OPACITY
                )}
                aria-label="Edit status or reason"
              >
                <i className="fas fa-edit tw:text-xs" />
              </button>
            </div>

            {/* Row 5: Quick edit fields */}
            <div className="tw:grid tw:gap-2 tw:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
              <div
                className="tw:flex tw:min-w-0 tw:flex-col tw:gap-1"
                data-testid="opportunity-max-providers"
              >
                <span className="tw:text-sm tw:text-zinc-400 tw:dark:text-white">
                  Max Providers
                </span>
                <InlineTextEdit
                  permission="update__assignments__opportunity"
                  className="tw:w-full tw:min-w-0"
                  value={maxAcceptedProviders}
                  onChange={handleMaxAcceptedProvidersChange}
                  onBlur={handleMaxAcceptedProvidersBlur}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const value = (e.target as HTMLInputElement).value;
                      handleMaxAcceptedProvidersBlur(value);
                    }
                  }}
                  placeholder="Leave blank for unlimited..."
                  displayValue={(val) => val || 'Unlimited'}
                  small
                  disabled={isClosed || updateMaxAcceptedProvidersMutation.isPending}
                  type="text"
                  inputMode="numeric"
                />
              </div>
              <div
                className="tw:flex tw:min-w-0 tw:flex-col tw:gap-1"
                data-testid="opportunity-start-date"
              >
                <span className="tw:text-sm tw:text-zinc-400 tw:dark:text-white">Start Date</span>
                <InlineDateEdit
                  permission="update__assignments__opportunity"
                  className="tw:w-full tw:min-w-0"
                  value={startDate}
                  onChange={handleStartDateChange}
                  onBlur={handleStartDateBlur}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleStartDateBlur(startDate);
                  }}
                  placeholder="Select start date"
                  small
                  disabled={isClosed || updateStartDateMutation.isPending}
                />
              </div>
              <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-1">
                <span className="tw:text-sm tw:text-zinc-400 tw:dark:text-white">Auto-Match</span>
                <div
                  className="tw:flex tw:items-center tw:gap-3"
                  data-testid="opportunity-auto-match-switch"
                >
                  <BrandSwitch
                    checked={autoMatch ?? false}
                    onChange={handleAutoMatchChange}
                    disabled={isClosed || updateAutoMatchMutation.isPending}
                    small
                    data-testid="opportunity-auto-match-toggle"
                  />
                  <span className="tw:text-sm tw:text-zinc-500">{autoMatch ? 'On' : 'Off'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Close Button (grid layout only) */}
          {onClose && (
            <div className="tw:hidden tw:sm:flex tw:justify-end tw:items-start tw:sm:col-span-1 tw:sm:justify-self-end">
              <DrawerCloseButton onClick={onClose} data-testid="opportunity-drawer-close" />
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="tw:flex tw:flex-wrap tw:gap-2 tw:mt-2 tw:justify-end">
          <StatusTransitionButtons
            isLoading={isLoadingOpportunity}
            currentStatusCode={opportunity?.current_status?.code}
            workflowMetadata={workflowStatuses as StatusMetadata}
            onStatusClick={(status) => {
              setPendingTargetStatus(status.code);
              setIsStatusModalOpen(true);
            }}
            small={isMobile}
            isPending={statusUpdateMutation.isPending}
            disabledStatusMap={disabledStatusMap}
          />
          {isClosed && (
            <Button small={isMobile} color="brand-green-outline" onClick={handleDuplicateClick}>
              Duplicate
            </Button>
          )}
          {canInviteProvider && (
            <Button
              small={isMobile}
              color="brandgreen1"
              onClick={() => setIsInviteProviderOpen(true)}
            >
              <i className="fas fa-fw fa-user-plus" />
              Invite 1099 Provider
            </Button>
          )}
        </div>

        {/* Details and Connections */}
        <Tabs activeTab={activeTab} onChange={setActiveTab} className="tw:mt-4">
          <div className="tw:flex tw:flex-col tw:w-full">
            {isOverflowCollapsed ? (
              <div className="tw:pb-2">
                <div className="tw:text-xs tw:text-zinc-500 tw:mb-1">Select View</div>
                <DropdownSelect
                  inputId="opportunity-drawer-section"
                  aria-label="Opportunity section"
                  allOptions={tabs.map((t) => ({ value: t.code, label: t.label }))}
                  value={activeTab}
                  isSearchable={false}
                  isClearable={false}
                  onChange={(opt: CodeDefinitionOption | CodeDefinitionOption[] | null) => {
                    if (opt && !Array.isArray(opt)) setActiveTab(opt.value);
                  }}
                />
              </div>
            ) : null}
            <div
              aria-hidden={isOverflowCollapsed}
              ref={tabsMeasureRef}
              className={
                isOverflowCollapsed
                  ? 'tw:invisible tw:max-h-0 tw:overflow-hidden tw:pointer-events-none'
                  : undefined
              }
            >
              <TabsList
                className={
                  isOverflowCollapsed
                    ? 'tw:border-transparent tw:dark:!border-transparent'
                    : undefined
                }
              >
                {tabs.map((t) => (
                  <TabsButton key={t.code} tab={t.code}>
                    {t.label}
                  </TabsButton>
                ))}
              </TabsList>
            </div>
          </div>
          <TabsContent tab="details">
            <div className="tw:flex tw:justify-between tw:items-start tw:gap-6">
              <div className="tw:flex-1 tw:space-y-4">
                {opportunity?.need && (
                  <section>
                    <div className="tw:mb-4">
                      <a
                        href={`${needsListBasePath}?id=${opportunity?.need?.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tw:mt-0 tw:!text-sm tw:text-brand-green-0 hover:tw:underline tw:text-left tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer tw:dark:text-brand-green-1"
                      >
                        <i className="far fa-arrow-up-right-from-square tw:mr-2" />
                        Linked Need: {opportunity?.need?.code}
                      </a>
                    </div>
                  </section>
                )}

                {/* Basic Information Section */}
                <section>
                  <DrawerSectionHeader
                    text="Basic Information"
                    icon={
                      <i className="fas fa-info-circle tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                    }
                  />
                  <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
                    <DrawerField
                      label="Estimated Duration"
                      value={opportunity.estimated_duration}
                    />
                    <DrawerField
                      label="Description"
                      value={opportunity.description}
                      whitespacePreLine
                    />
                    <DrawerField
                      label="Additional Facilities"
                      value={
                        opportunity.alternate_facilities &&
                        Array.isArray(opportunity.alternate_facilities) &&
                        opportunity.alternate_facilities.length > 0
                          ? opportunity.alternate_facilities
                              .map((facility) =>
                                typeof facility === 'string' ? facility : facility.name
                              )
                              .join(', ')
                          : null
                      }
                    />
                  </div>
                </section>

                {/* Professional Requirements Section */}
                <section>
                  <DrawerSectionHeader
                    text="Professional Requirements"
                    icon={
                      <i className="fas fa-stethoscope tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                    }
                  />
                  <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
                    <DrawerField
                      label="Degrees"
                      value={
                        opportunity.degrees && opportunity.degrees.length > 0
                          ? opportunity.degrees.map((deg) => degreeMap[deg] || deg).join(', ')
                          : null
                      }
                    />
                    <DrawerField
                      label="Availability"
                      value={
                        opportunity.work_types && opportunity.work_types.length > 0
                          ? opportunity.work_types.map((workType) => workType).join(', ')
                          : null
                      }
                    />
                    <DrawerField
                      label="Life Support Certifications"
                      value={
                        opportunity.life_support_certifications &&
                        opportunity.life_support_certifications.length > 0 ? (
                          opportunity.life_support_certifications.map((cert) => cert).join(', ')
                        ) : (
                          <span className="tw:text-stoplight-warning">
                            <i className="fas fa-exclamation-triangle tw:text-stoplight-warning tw:dark:text-stoplight-warning" />{' '}
                            Required to Publish
                          </span>
                        )
                      }
                    />
                    <DrawerField
                      label="Board Certifications"
                      value={
                        opportunity.board_certifications &&
                        opportunity.board_certifications.length > 0 ? (
                          opportunity.board_certifications
                            .map((cert) =>
                              cert.boardEligible ? `${cert.value} (Allow Eligible)` : cert.value
                            )
                            .join(', ')
                        ) : (
                          <span className="tw:text-stoplight-warning">
                            <i className="fas fa-exclamation-triangle tw:text-stoplight-warning tw:dark:text-stoplight-warning" />{' '}
                            Required to Publish
                          </span>
                        )
                      }
                    />
                  </div>
                </section>

                {/* Eligibility Requirements Section */}
                <section>
                  <DrawerSectionHeader
                    text="Eligibility Requirements"
                    icon={
                      <i className="fas fa-check-circle tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                    }
                  />
                  <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
                    <DrawerField
                      label="Allow IMLC Providers"
                      value={opportunity.allow_imlc ? 'Yes' : 'No'}
                    />
                    <DrawerField
                      label="Allow Malpractice History"
                      value={opportunity.allow_malpractice_history ? 'Yes' : 'No'}
                    />
                    <DrawerField
                      label="Years of Experience"
                      value={opportunity.years_of_experience}
                    />
                    <DrawerField label="Shifts Completed" value={opportunity.shifts_completed} />
                    <DrawerField label="Additional Info" value={opportunity.additional_info} />
                  </div>
                </section>

                {/* Opportunity Settings Section */}
                <section>
                  <DrawerSectionHeader
                    text="Opportunity Settings"
                    icon={
                      <i className="fas fa-cog tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                    }
                  />
                  <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
                    <DrawerField
                      label="Shift Rate Types"
                      value={
                        opportunity.shift_rate_types && opportunity.shift_rate_types.length > 0 ? (
                          <span className="tw:flex tw:flex-wrap tw:gap-x-1">
                            {opportunity.shift_rate_types.map((shiftRateType, index) => {
                              const shiftRateTypeLabel =
                                shiftRateTypeMap[shiftRateType] || shiftRateType;
                              const shiftTypeToSettingsKey: Record<string, string> = {
                                day: 'max_rate_day',
                                night: 'max_rate_night',
                                call: 'max_rate_call',
                                '24h': 'max_rate_24h',
                              };
                              const rateKey = shiftTypeToSettingsKey[shiftRateType];
                              const rate = opportunity.settings?.[
                                rateKey as keyof typeof opportunity.settings
                              ] as number | null | undefined;
                              return (
                                <React.Fragment key={shiftRateType}>
                                  {index > 0 && <span>,&nbsp;</span>}
                                  <span>
                                    {shiftRateTypeLabel}
                                    {rate ? (
                                      `: $${rate}`
                                    ) : (
                                      <span className="tw:text-stoplight-warning tw:italic tw:ml-1 tw:text-xs">
                                        <i className="fas fa-exclamation-triangle tw:mr-1" />
                                        Max {shiftRateTypeLabel} Rate Required to Publish
                                      </span>
                                    )}
                                  </span>
                                </React.Fragment>
                              );
                            })}
                          </span>
                        ) : (
                          <span className="tw:text-stoplight-warning">
                            <i className="fas fa-exclamation-triangle tw:text-stoplight-warning tw:dark:text-stoplight-warning" />{' '}
                            Required to Publish
                          </span>
                        )
                      }
                    />
                    <DrawerField
                      label="Created"
                      value={
                        opportunity.created_at
                          ? formatDateTo24Hr(opportunity.created_at, 'MM/dd/yyyy HH:mm')
                          : null
                      }
                    />
                    <DrawerField
                      label="Last Updated"
                      value={
                        opportunity.updated_at
                          ? formatDateTo24Hr(opportunity.updated_at, 'MM/dd/yyyy HH:mm')
                          : null
                      }
                    />
                  </div>
                </section>
              </div>
              {opportunity.current_status?.title !== 'Closed' && (
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(true)}
                  className="tw:text-brand-green-0 tw:hover:text-brand-green-0/80 tw:transition-colors tw:cursor-pointer"
                  aria-label="Edit opportunity"
                >
                  <i className="fa-solid fa-pen-to-square tw:text-lg" />
                </button>
              )}
            </div>
          </TabsContent>
          {opportunityFacilityId && (
            <TabsContent tab="facility-info">
              <div className="tw:pt-2">
                {isFacilityPanelLoading || !enrichedOpportunityFacility ? (
                  <div className="tw:text-sm tw:text-zinc-500 tw:dark:text-zinc-400">
                    Loading facility details...
                  </div>
                ) : (
                  <FacilityDetailsPanel
                    facility={enrichedOpportunityFacility}
                    codeDefMaps={facilityPanelCodeDefMaps}
                    canEdit={canEditFacility}
                    onEditClick={() => setIsFacilityEditDialogOpen(true)}
                    facilityId={opportunityFacilityId}
                  />
                )}
              </div>
            </TabsContent>
          )}
          <TabsContent tab="specialty">
            <div className="tw:pt-2">
              {isFacilitySpecialtiesLoading ? (
                <div className="tw:text-sm tw:text-zinc-500">Loading specialty details...</div>
              ) : (
                <div className="tw:relative tw:bg-white tw:rounded-xl tw:border tw:border-zinc-200 tw:p-6 tw:shadow-sm">
                  <div className="tw:flex tw:items-start tw:gap-3">
                    <div className="tw:flex-1">
                      <h6 className="tw:text-lg tw:font-semibold tw:text-zinc-900 tw:!mb-0">
                        {specialtyDrawerLabel}
                      </h6>
                      {!opportunityFacilitySpecialty?.dynamic_fields?.length && (
                        <div className="tw:mt-2 tw:text-sm tw:text-zinc-500">
                          Please{' '}
                          {window.SENDIT_CONTACT_INFO?.healthcare_entity_support_email ? (
                            <a
                              href={`mailto:${window.SENDIT_CONTACT_INFO.healthcare_entity_support_email}`}
                              className="tw:text-brand-green-1 tw:!no-underline hover:tw:underline"
                            >
                              contact support
                            </a>
                          ) : (
                            'contact support'
                          )}{' '}
                          to configure this specialty.
                        </div>
                      )}
                    </div>

                    {canEditFacility &&
                      opportunityFacilitySpecialty?.id &&
                      (opportunityFacilitySpecialty?.dynamic_fields?.length ?? 0) > 0 && (
                        <div className="tw:shrink-0">
                          <Button
                            type="button"
                            plain={true}
                            onClick={() => setIsEditSpecialtyDialogOpen(true)}
                            className="tw:text-zinc-400 tw:hover:text-brand-green-0 tw:p-2"
                            aria-label={`Edit ${specialtyDrawerLabel}`}
                          >
                            <i className="fa-regular fa-pen-to-square" />
                          </Button>
                        </div>
                      )}
                  </div>

                  {opportunityFacilitySpecialty?.dynamic_fields?.length ? (
                    <div className="tw:mt-4 tw:bg-zinc-50 tw:rounded-lg tw:p-4">
                      <DynamicFieldFactory
                        fields={opportunityFacilitySpecialty.dynamic_fields}
                        onChange={() => {}}
                        readOnly={true}
                        columns={2}
                      />
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {opportunityFacilitySpecialty && (
          <EditSpecialtyDialog
            open={isEditSpecialtyDialogOpen}
            onClose={() => setIsEditSpecialtyDialogOpen(false)}
            specialty={
              opportunityFacilitySpecialty as unknown as FacilitySpecialty & { title?: string }
            }
            onSave={async () => Promise.resolve()}
            onSuccess={() => {
              if (opportunityFacilityId) {
                queryClient.invalidateQueries({
                  queryKey: ['facilitySpecialties', opportunityFacilityId],
                });
              }
              setIsEditSpecialtyDialogOpen(false);
            }}
          />
        )}

        <div className="tw:mt-2 tw:p-2 tw:bg-brand-green-3 tw:rounded-lg">
          <SectionHeader className="tw:mb-2 tw:p-1 tw:text-brand-green-0" headerText="Activity" />
          <ActivityLog
            notes={notesData}
            enableCommentsFilter={true}
            enableStatusChangesFilter={true}
            enableUpdatesFilter={true}
            enableEventsFilter={false}
            statusHistory={statusHistory as ActivityStatusHistory[] | undefined}
            changeHistory={changeHistory as ActivityHistory[] | undefined}
            composerPlaceholder="Enter a comment..."
            noteMutations={{
              createNote: async (data) => {
                return AssignmentsService.assignmentsOpportunitiesNotesCreate({
                  id: opportunityId,
                  requestBody: {
                    opportunity: opportunityId,
                    comment: data.comment,
                  } as OpportunityNote,
                });
              },
              updateNote: async (noteId: string, data: PatchedOpportunityNote) => {
                return AssignmentsService.assignmentsOpportunitiesNotePartialUpdate({
                  id: noteId,
                  requestBody: data,
                });
              },
              deleteNote: async (noteId: string) => {
                return AssignmentsService.assignmentsOpportunitiesNoteDestroy({
                  id: noteId,
                });
              },
              notesQueryKey: [OPPORTUNITY_NOTES_QUERY_KEY, opportunityId],
              extraInvalidateQueryKeys: [[OPPORTUNITY_STATUS_HISTORY_QUERY_KEY, opportunityId]],
            }}
          />
        </div>
      </div>
    </>
  );
}
